import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { RootState } from '../../app/store';
import { LOADING_STATUS } from '../../common/constants';
import {
  fetchAllTenants,
  fetchSiteDetails,
  fetchTenantDetails,
  updateTenant,
} from '../../common/api/officesService';
import {
  fetchAccommodationDetails,
  updateAccommodation,
} from '../../common/api/accommodationsService';
import { AccommodationDetailsUpdate } from '../accommodations/accommodationsSlice';

export interface OfficeListItem {
  id: string;
  name: string;
}

export interface OfficeUpdateData {
  id: string;
  name: string;
}
export interface SiteBankData {
  id: string;
  accountId: string | null;
  iban: string;
  bic: string;
  name: string;
}

export interface SiteDetailsResponse {
  id: string;
  postPostCode: string;
  postCity: string;
  postStreetName: string;
  phone: string;
  email: string;
  name: string;
  www: string;
  ycode: string;
  siteCustomerName: string;
  siteCustomerAddress: string;
  siteCustomerPostAddress: string;
  wintimeSaleVoucherType: string;
  wintimePurchaseVoucherType: string;
  wintimeAdvanceFeeSalesVoucherType: string;
  referenceNumberPrefix: string;
  purchaseDeptAccountNumber: number;
  salesClaimAccountNumber: number;
  wintimeSiteName: string;
  advanceFeeAdvanceSalesAccountNumber: number;
  advanceFeeSalesAccountNumber: number;
  delayFeeP: number;
  interHomeadvanceBillRule1P: number | null;
  advanceBillRule1P: number | null;
  advanceBillRule1MaxPrice: number | null;
  foreignAdvanceBillRule1MaxPrice: number | null;
  advanceBillRule2BookingLenght: number | null;
  advanceBillRule2P: number | null;
  advanceBillRule2MaxPrice: number | null;
  advanceBillRule3PriceLimit: number | null;
  advanceBillRule3MaxPrice: number | null;
  officeFeeAmount: number;
  officeFeeName: string;
  ifCancellationPercentage: number;
  officeFeeVatP: number;
  ifCancellationName: string;
  ifCancellationNumber: string;
  officeFeeNumber: string;
  latestWebShopDate: string;
  latestApiDate: string | null;
  siteCustomerNumber: string | null;
  siteAsOwnerAccountNumber: string | null;
  ownerDueDateDayAmount: number;
  partnerSiteOwnerDueDateDayAmount: number;
  siteBankData: SiteBankData[];
}
export interface OfficeDetails {
  id: string;
  name: string;
  wintimeSaleVoucherType: string;
  wintimePurchaseVoucherType: string;
  wintimeAdvanceFeeSalesVoucherType: string;
  referenceNumberPrefix: string;
  purchaseDeptAccountNumber: number;
  salesClaimAccountNumber: number;
  wintimeSiteName: string;
  advanceFeeAdvanceSalesAccountNumber: number;
  advanceFeeSalesAccountNumber: number;
  delayFeeP: number;
}

export interface TenantsQueryFilters {
  searchTerm: string;
}

export interface OfficesState {
  tenantItems: OfficeListItem[];
  tenantDetails: OfficeDetails;
  siteDetails: SiteDetailsResponse;
  tenantsStatus:
    | LOADING_STATUS.IDLE
    | LOADING_STATUS.LOADING
    | LOADING_STATUS.FAILED;

  tenantsDetailStatus:
    | LOADING_STATUS.IDLE
    | LOADING_STATUS.LOADING
    | LOADING_STATUS.FAILED;
  tenantsFilter: TenantsQueryFilters;
}

const initialState: OfficesState = {
  tenantItems: [],
  tenantsStatus: LOADING_STATUS.IDLE,
  tenantsFilter: null,
  tenantsDetailStatus: LOADING_STATUS.IDLE,
  tenantDetails: null,
  siteDetails: null,
};

export const getAllTenantsAsync = createAsyncThunk(
  'tenants/getAllTenants',
  async (filterCriteria: TenantsQueryFilters, { rejectWithValue }) => {
    try {
      // Do some cheking;
      const response = await fetchAllTenants(filterCriteria);
      return response.data;
    } catch (error) {
      // Use custom error response
      return rejectWithValue(error.response.data);
    }
  },
);

export const getTenantDetailsAsync = createAsyncThunk(
  'tenants/getTenantDetails',
  async (tenantId: string, { rejectWithValue }) => {
    try {
      const response = await fetchTenantDetails(tenantId);
      return response.result;
    } catch (error) {
      // Use custom error response
      return rejectWithValue(error.response.data);
    }
  },
);

export const getSiteDetailsAsync = createAsyncThunk(
  'tenants/getSiteDetails',
  async (siteId: string, { rejectWithValue }) => {
    try {
      const response = await fetchSiteDetails(siteId);
      return response.data.result;
    } catch (error) {
      // Use custom error response
      return rejectWithValue(error.response.data);
    }
  },
);

export const updateTenantAsync = createAsyncThunk(
  'accommodations/updateAccommodation',
  async (accommodationData: OfficeUpdateData, { rejectWithValue }) => {
    try {
      const response = await updateTenant(accommodationData);
      return response.result;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

const OfficesSlice = createSlice({
  name: 'tenants',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(getAllTenantsAsync.pending, state => {
        state.tenantsStatus = LOADING_STATUS.LOADING;
      })
      .addCase(getAllTenantsAsync.fulfilled, (state, action) => {
        state.tenantsStatus = LOADING_STATUS.IDLE;
        state.tenantItems = action.payload;
      })
      .addCase(getAllTenantsAsync.rejected, state => {
        state.tenantsStatus = LOADING_STATUS.FAILED;
      })
      .addCase(getTenantDetailsAsync.pending, state => {
        state.tenantsDetailStatus = LOADING_STATUS.LOADING;
      })
      .addCase(getTenantDetailsAsync.fulfilled, (state, action) => {
        state.tenantsDetailStatus = LOADING_STATUS.IDLE;
        state.tenantDetails = action.payload;
      })
      .addCase(getTenantDetailsAsync.rejected, state => {
        state.tenantsDetailStatus = LOADING_STATUS.FAILED;
      })
      .addCase(getSiteDetailsAsync.pending, state => {
        state.tenantsDetailStatus = LOADING_STATUS.LOADING;
      })
      .addCase(getSiteDetailsAsync.fulfilled, (state, action) => {
        state.tenantsDetailStatus = LOADING_STATUS.IDLE;
        state.siteDetails = action.payload;
      })
      .addCase(getSiteDetailsAsync.rejected, state => {
        state.tenantsDetailStatus = LOADING_STATUS.FAILED;
      });
  },
});

export const selectTenants = (state: RootState) => state.tenants.tenantItems;
export const selectTenantsLoadingStatus = (state: RootState) =>
  state.tenants.tenantsStatus;

export const selectTenantDetails = (state: RootState) =>
  state.tenants.tenantDetails;

export const selectSiteDetails = (state: RootState) =>
  state.tenants.siteDetails;
export const selectTenantDetailsLoadingStatus = (state: RootState) =>
  state.tenants.tenantsDetailStatus;
export default OfficesSlice.reducer;
